import React, { Suspense, lazy } from 'react'
import { Route, Routes, Navigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { SideBarContext } from '../context/Shared/SideBar.context'
import { FlowVUContextProvider } from '../context/FlowVU/FlowVU.context'
import { FileVUContextProvider } from '../context/FileVU/FileVU.context'
import { VideoVUContextProvider } from '../context/VideoVU/VideoVU.context'
import { SignOut } from '../pages/SignOut/SignOut.page'
import PropTypes from 'prop-types'
import './AppRouter.style.scss'
import { theme } from '../styles/theme.style'
import { routify } from '../utils/functions'
import { PatientVUContextProvider } from '../context/PatientVU/PatientVU.context'
import { CustomAgentContextProvider } from '../context/CustomAgent/CustomAgent.context'
import { RemoteVUContextProvider } from '../context/RemoteVU/RemoteVU.context'
import { GlassVUContextProvider } from '../context/GlassVU/GlassVU.context'
import { CamVUContextProvider } from '../context/CamVU/CamVU.context'
import { HomeVUContextProvider } from '../context/HomeVU/HomeVU.context'
import RoleGuard from '../components/Shared/RoleGuard/RoleGuard.component'

const HomeVU = lazy(() => import('../pages/HomeVU/HomeVU.page'))
const GlassVU = lazy(() => import('../pages/GlassVU/GlassVU.page.jsx'))
const RemoteVU = lazy(() => import('../pages/RemoteVU/RemoteVU.page'))
const ScreenVU = lazy(() => import('../pages/ScreenVU/ScreenVU.page'))
const CamVU = lazy(() => import('../pages/CamVU/CamVU.page'))
const GroupVU = lazy(() => import('../pages/GroupVU/GroupVU.page'))
const FileVU = lazy(() => import('../pages/FileVU/FileVU.page'))
const VideoVU = lazy(() => import('../pages/VideoVU/VideoVU.page'))
const CreateTagPage = lazy(() => import('../pages/VideoVU/CreateTag.page'))
const ReportVU = lazy(() => import('../pages/ReportVU/ReportVU.page'))
const SettingsPage = lazy(() => import('../pages/Settings/Settings.page'))
const PortalVU = lazy(() => import('../pages/PortalVU/PortalVU.page'))
const PatientVU = lazy(() => import('../pages/PatientVU/PatientVU.page'))
const PatientDetail = lazy(
  () => import('../pages/PatientVU/PatientDetail.page')
)
const Session = lazy(() => import('../pages/Session/Session.page'))
const FlowvuDashboard = lazy(
  () => import('../pages/FlowVU/FlowvuDashboard.page')
)
const Flowvu = lazy(() => import('../pages/FlowVU/FlowvuContainer.page.jsx'))
const CreateFlowvu = lazy(() => import('../pages/FlowVU/CreateFlowvu.page'))
const ChatVU = lazy(() => import('../pages/ChatVU/ChatVU.page'))
const CalendarVU = lazy(() => import('../pages/CalendarVU/CalendarVU.page'))
const LicenceVU = lazy(() => import('../pages/LicenceVU/LicenceVU.page'))
const LibraryVU = lazy(() => import('../pages/LibraryVU/LibraryVU.page'))
const DevicesVU = lazy(() => import('../pages/DevicesVU/DevicesVU.page'))
const AgentVU = lazy(() => import('../pages/AgentVU/AgentVU.page'))

const ADMIN_ROLES = ['Super User', 'Manager']
const CALL_ROLES = ['Super User', 'User', 'Viewer', 'Guest', 'Education']
const FILE_ROLES = ['Super User', 'Manager', 'User', 'Education']
const PATIENT_ROLES = ['Super User', 'Manager', 'User']
const NON_VIEWER_ROLES = ['Super User', 'Manager', 'User', 'Guest', 'Education']
const NON_VIEWER_EDU_ROLES = ['Super User', 'Manager', 'User', 'Guest']

const AppRoutes = ({
  online,
  onlineStatus,
  initiateCall,
  addRemoteVUToCall,
  openSnackBar,
  addGlassVUToCall,
  userBeingCalledId,
  joinCall
}) => {
  const isRTL = document.body.dir === 'rtl'
  const { sideBarVisibility } = React.useContext(SideBarContext)
  const { t } = useTranslation()
  return (
    <div
      className={
        sideBarVisibility
          ? isRTL
            ? 'routesContainerSidebarRight'
            : 'routesContainerSideBarLeft'
          : 'routesContainer'
      }
    >
      <Routes>
        <Route
          path="/"
          exact
          element={<Navigate replace to={routify(theme.HOMEVU)} />}
        ></Route>
        <Route
          path={'/' + routify(theme.HOMEVU)}
          element={
            <Suspense fallback={<>...</>}>
              <HomeVU onlineUsers={online} onlineStatus={onlineStatus} initiateCall={initiateCall} />
            </Suspense>
          }
        />
        <Route
          path={'/' + routify(theme.GLASSVU)}
          element={
            <Suspense fallback={<>...</>}>
              <RoleGuard allowedRoles={ADMIN_ROLES}>
                <GlassVU
                  online={online}
                  initiateCall={initiateCall}
                  userBeingCalledId={userBeingCalledId}
                />
              </RoleGuard>
            </Suspense>
          }
        />
        <Route
          path={'/' + routify(theme.REMOTEVU)}
          element={
            <Suspense fallback={<>...</>}>
              <RoleGuard allowedRoles={ADMIN_ROLES}>
                <RemoteVU
                  online={online}
                  initiateCall={initiateCall}
                  userBeingCalledId={userBeingCalledId}
                />
              </RoleGuard>
            </Suspense>
          }
        />
        <Route
          path={'/' + routify(theme.SCREENVU)}
          element={
            <Suspense fallback={<>...</>}>
              <RoleGuard allowedRoles={ADMIN_ROLES}>
                <ScreenVU
                  online={online}
                  initiateCall={initiateCall}
                  userBeingCalledId={userBeingCalledId}
                />
              </RoleGuard>
            </Suspense>
          }
        />
        <Route
          path={'/' + routify(theme.CAMVU)}
          element={
            <Suspense fallback={<>...</>}>
              <RoleGuard allowedRoles={ADMIN_ROLES}>
                <CamVU online={online} />
              </RoleGuard>
            </Suspense>
          }
        />
        <Route
          path={'/' + routify(theme.GROUPVU)}
          element={
            <Suspense fallback={<>...</>}>
              <RoleGuard allowedRoles={ADMIN_ROLES}>
                <GroupVU
                  online={online}
                  userBeingCalledId={userBeingCalledId}
                  joinCall={joinCall}
                  addGlassVUToCall={addGlassVUToCall}
                  addRemoteVUToCall={addRemoteVUToCall}
                />
              </RoleGuard>
            </Suspense>
          }
        />
        <Route
          path={'/' + routify(theme.FILEVU)}
          element={
            <Suspense fallback={<>...</>}>
              <RoleGuard allowedRoles={FILE_ROLES}>
                <FileVUContextProvider>
                  <VideoVUContextProvider>
                    <FileVU />
                  </VideoVUContextProvider>
                </FileVUContextProvider>
              </RoleGuard>
            </Suspense>
          }
        />
        <Route
          path={'/' + routify(theme.VIDEOVU)}
          element={
            <Suspense fallback={<>...</>}>
              <RoleGuard allowedRoles={FILE_ROLES}>
                <VideoVUContextProvider>
                  <FileVUContextProvider>
                    <VideoVU />
                  </FileVUContextProvider>
                </VideoVUContextProvider>
              </RoleGuard>
            </Suspense>
          }
        />
        <Route
          path={'/' + routify(theme.VIDEOVU) + '/create-tags'}
          element={
            <Suspense fallback={<>...</>}>
              <RoleGuard allowedRoles={FILE_ROLES}>
                <VideoVUContextProvider>
                  <CreateTagPage />
                </VideoVUContextProvider>
              </RoleGuard>
            </Suspense>
          }
        />
        <Route
          path={'/' + routify(theme.REPORTVU)}
          element={
            <Suspense fallback={<>...</>}>
              <RoleGuard allowedRoles={ADMIN_ROLES}>
                <PatientVUContextProvider>
                  <ReportVU />
                </PatientVUContextProvider>
              </RoleGuard>
            </Suspense>
          }
        />
        <Route
          path={'/' + routify(theme.LICENCEVU)}
          element={
            <Suspense fallback={<>...</>}>
              <RoleGuard allowedRoles={ADMIN_ROLES}>
                <LicenceVU />
              </RoleGuard>
            </Suspense>
          }
        />
        <Route
          path={'/' + routify(theme.CALENDARVU)}
          element={
            <Suspense fallback={<>...</>}>
              <RoleGuard allowedRoles={NON_VIEWER_EDU_ROLES}>
                <RemoteVUContextProvider>
                  <GlassVUContextProvider>
                    <CamVUContextProvider>
                      <HomeVUContextProvider>
                        <CalendarVU />
                      </HomeVUContextProvider>
                    </CamVUContextProvider>
                  </GlassVUContextProvider>
                </RemoteVUContextProvider>
              </RoleGuard>
            </Suspense>
          }
        />
        <Route
          path={'/' + routify(theme.CHATVU)}
          element={
            <Suspense fallback={<>...</>}>
              <RoleGuard allowedRoles={NON_VIEWER_ROLES}>
                <ChatVU />
              </RoleGuard>
            </Suspense>
          }
        />
        <Route
          path={'/' + routify(theme.LIBRARYVU)}
          element={
            <Suspense fallback={<>...</>}>
              <RoleGuard allowedRoles={FILE_ROLES}>
                <FileVUContextProvider>
                  <LibraryVU />
                </FileVUContextProvider>
              </RoleGuard>
            </Suspense>
          }
        />
        <Route
          path={'/' + routify(theme.DEVICESVU)}
          element={
            <Suspense fallback={<>...</>}>
              <RoleGuard allowedRoles={ADMIN_ROLES}>
                <DevicesVU
                  online={online}
                  initiateCall={initiateCall}
                  userBeingCalledId={userBeingCalledId}
                />
              </RoleGuard>
            </Suspense>
          }
        />
        <Route
          path={'/' + routify(theme.PORTALVU)}
          element={
            <Suspense fallback={<>...</>}>
              <RoleGuard allowedRoles={[]}>
                <PortalVU />
              </RoleGuard>
            </Suspense>
          }
        />
        <Route
          path={'/' + routify(theme.PATIENTVU)}
          element={
            <Suspense fallback={<>...</>}>
              <RoleGuard allowedRoles={PATIENT_ROLES}>
                <PatientVU />
              </RoleGuard>
            </Suspense>
          }
        />
        <Route
          path={'/' + routify(theme.PATIENTVU) + '/:patientId'}
          element={
            <Suspense fallback={<>...</>}>
              <RoleGuard allowedRoles={PATIENT_ROLES}>
                <PatientVUContextProvider>
                  <PatientDetail />
                </PatientVUContextProvider>
              </RoleGuard>
            </Suspense>
          }
        />
        <Route
          path={'/' + routify(theme.AGENTVU)}
          element={
            <Suspense fallback={<>...</>}>
              <RoleGuard allowedRoles={NON_VIEWER_ROLES}>
                <CustomAgentContextProvider>
                  <AgentVU />
                </CustomAgentContextProvider>
              </RoleGuard>
            </Suspense>
          }
        />
        <Route
          path={'/' + routify(theme.FLOWVU)}
          exact
          element={
            <Suspense fallback={<>...</>}>
              <RoleGuard allowedRoles={NON_VIEWER_ROLES}>
                <FlowVUContextProvider>
                  <FlowvuDashboard />
                </FlowVUContextProvider>
              </RoleGuard>
            </Suspense>
          }
        />
        <Route
          path={'/' + routify(theme.FLOWVU) + '/create'}
          exact
          element={
            <Suspense fallback={<>...</>}>
              <RoleGuard allowedRoles={NON_VIEWER_ROLES}>
                <FlowVUContextProvider>
                  <CreateFlowvu />
                </FlowVUContextProvider>
              </RoleGuard>
            </Suspense>
          }
        />
        <Route
          path="/workflows/:flowvuId"
          element={
            <Suspense fallback={<>...</>}>
              <RoleGuard allowedRoles={NON_VIEWER_ROLES}>
                <FlowVUContextProvider>
                  <Flowvu />
                </FlowVUContextProvider>
              </RoleGuard>
            </Suspense>
          }
        />
        <Route
          path={'/' + routify(theme.SETTINGS)}
          element={
            <Suspense fallback={<>...</>}>
              <SettingsPage />
            </Suspense>
          }
        />
        <Route
          path={'/' + routify(theme.SESSION)}
          element={
            <Suspense fallback={<>...</>}>
              <RoleGuard allowedRoles={CALL_ROLES} blockedMessage={t('Managers are not allowed to join sessions')}>
                <PatientVUContextProvider>
                  <Session
                    online={online}
                    addRemoteVUToCall={addRemoteVUToCall}
                    openSnackBar={openSnackBar}
                    addGlassVUToCall={addGlassVUToCall}
                  />
                </PatientVUContextProvider>
              </RoleGuard>
            </Suspense>
          }
        />
        <Route path={'/' + theme.LOGOUT} exact element={<SignOut />}></Route>
      </Routes>
    </div>
  )
}

AppRoutes.propTypes = {
  online: PropTypes.object,
  initiateCall: PropTypes.func,
  addRemoteVUToCall: PropTypes.func,
  openSnackBar: PropTypes.func,
  addGlassVUToCall: PropTypes.func,
  userBeingCalledId: PropTypes.string,
  joinCall: PropTypes.func
}

export default React.memo(AppRoutes)
